<template>
  <div class="settings-overlay" @click.self="close">
    <div class="settings-dialog">
      <div class="settings-header">
        <h4>Device Settings</h4>
        <button class="settings-close-btn" type="button" @click="close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="settings-body">
        <label class="settings-label">Device Name</label>
        <input
          v-model="settingsDeviceName"
          type="text"
          class="settings-input"
          placeholder="Enter device name"
          @keyup.enter="saveSettings"
        />
        <label class="settings-label settings-gap">Sensor 1</label>
        <input
          v-model="settingsSensor1"
          type="text"
          maxlength="16"
          class="settings-input"
          placeholder="Sensor 1"
          @keyup.enter="saveSettings"
        />
        <label class="settings-label settings-gap">Sensor 2</label>
        <input
          v-model="settingsSensor2"
          type="text"
          maxlength="16"
          class="settings-input"
          placeholder="Sensor 2"
          @keyup.enter="saveSettings"
        />
        <label class="settings-label settings-gap">Notes</label>
        <textarea
          v-model="settingsNotes"
          class="settings-input settings-notes"
          rows="3"
        ></textarea>
        <label class="settings-label settings-gap">Thermostat location</label>
        <textarea
          v-model="settingsThermostatLocation"
          class="settings-input settings-notes"
          rows="3"
          maxlength="500"
        ></textarea>
        <label class="settings-label settings-gap">Thermostat set point</label>
        <input
          v-model="settingsThermostatSetpoint"
          type="text"
          inputmode="numeric"
          maxlength="2"
          class="settings-input settings-setpoint"
          placeholder="72"
          @keyup.enter="saveSettings"
        />
        <label class="settings-label settings-gap">Wi-Fi name</label>
        <input
          v-model="settingsWifiName"
          type="text"
          maxlength="32"
          class="settings-input"
          autocomplete="off"
          @keyup.enter="saveSettings"
        />
        <label class="settings-label settings-gap">Wi-Fi password</label>
        <input
          v-model="settingsWifiPassword"
          type="text"
          maxlength="63"
          class="settings-input"
          autocomplete="off"
          @keyup.enter="saveSettings"
        />
        <label class="settings-label settings-gap">Sample Interval</label>
        <div class="interval-row">
          <input
            v-model.number="settingsIntervalValue"
            type="number"
            :min="settingsIntervalMin"
            class="settings-input interval-input"
            @keyup.enter="saveSettings"
          />
          <select v-model="settingsIntervalUnit" class="interval-unit-select">
            <option value="sec">sec</option>
            <option value="min">min</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
        </div>
        <p v-if="intervalTooShort" class="interval-hint">
          Interval must be longer than 5 seconds.
        </p>
      </div>
      <div class="settings-footer">
        <button
          type="button"
          class="settings-erase-btn"
          @click="eraseAllData"
          :disabled="settingsErasing || settingsSaving"
        >
          Erase all data
        </button>
        <button class="settings-cancel-btn" type="button" @click="close">
          Cancel
        </button>
        <button
          class="settings-save-btn"
          type="button"
          @click="saveSettings"
          :disabled="settingsSaving || intervalTooShort"
        >
          <i v-if="settingsSaving" class="fas fa-spinner fa-spin"></i>
          {{ settingsSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import axios from '@/axiosConfig';
import {
  eraseReadingsConfirmMessage,
  intervalInputMin,
  sampleIntervalAllowed,
  settingsIntervalFields,
} from '@/utils/tempChartDevices';

const props = defineProps({
  deviceId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['close', 'saved', 'erased']);

const settingsDeviceName = ref('');
const settingsSensor1 = ref('');
const settingsSensor2 = ref('');
const settingsNotes = ref('');
const settingsThermostatLocation = ref('');
const settingsThermostatSetpoint = ref('');
const settingsWifiName = ref('');
const settingsWifiPassword = ref('');
const settingsIntervalValue = ref(60);
const settingsIntervalUnit = ref('sec');
const settingsSaving = ref(false);
const settingsErasing = ref(false);
let active = true;

const settingsIntervalMin = computed(() =>
  intervalInputMin(settingsIntervalUnit.value),
);
const intervalTooShort = computed(
  () => !sampleIntervalAllowed(intervalToSeconds()),
);

function intervalToSeconds() {
  const v = settingsIntervalValue.value || 60;
  switch (settingsIntervalUnit.value) {
    case 'min':
      return v * 60;
    case 'hours':
      return v * 3600;
    case 'days':
      return v * 86400;
    default:
      return v;
  }
}

async function loadSettings() {
  try {
    const resp = await axios.get(`/api/sensors/devices/${props.deviceId}`);
    if (!active) return;
    const row = resp.data || {};
    settingsDeviceName.value = row.device_name || '';
    settingsSensor1.value = row.sensor1_name || '';
    settingsSensor2.value = row.sensor2_name || '';
    settingsNotes.value = row.notes || '';
    settingsThermostatLocation.value = row.thermostat_location || '';
    settingsThermostatSetpoint.value =
      row.thermostat_setpoint == null ? '' : String(row.thermostat_setpoint);
    settingsWifiName.value = row.wifi_name || '';
    settingsWifiPassword.value = row.wifi_password || '';
    const interval = settingsIntervalFields(row.sample_interval_sec);
    settingsIntervalValue.value = interval.value;
    settingsIntervalUnit.value = interval.unit;
  } catch (err) {
    console.error('Error loading device settings:', err);
    if (active) alert('Failed to load device settings.');
  }
}

function close() {
  emit('close');
}

async function saveSettings() {
  const newName = settingsDeviceName.value.trim();
  if (!newName || settingsSaving.value) return;

  const newIntervalSec = intervalToSeconds();
  if (!sampleIntervalAllowed(newIntervalSec)) return;

  settingsSaving.value = true;
  try {
    const resp = await axios.put(`/api/sensors/devices/${props.deviceId}`, {
      device_name: newName,
      sample_interval_sec: newIntervalSec,
      sensor1_name: settingsSensor1.value,
      sensor2_name: settingsSensor2.value,
      notes: settingsNotes.value,
      thermostat_location: settingsThermostatLocation.value,
      thermostat_setpoint: settingsThermostatSetpoint.value,
      wifi_name: settingsWifiName.value,
      wifi_password: settingsWifiPassword.value,
    });
    emit('saved', resp.data || {});
    emit('close');
  } catch (err) {
    console.error('Error saving device settings:', err);
    alert('Failed to save device settings.');
  } finally {
    settingsSaving.value = false;
  }
}

async function eraseAllData() {
  if (settingsErasing.value) return;
  const ok = window.confirm(
    eraseReadingsConfirmMessage(settingsDeviceName.value),
  );
  if (!ok) return;

  settingsErasing.value = true;
  try {
    await axios.delete(`/api/sensors/devices/${props.deviceId}/readings`);
    emit('erased');
    emit('close');
  } catch (err) {
    console.error('Error erasing device readings:', err);
    alert('Failed to erase device data.');
  } finally {
    settingsErasing.value = false;
  }
}

onMounted(loadSettings);
onUnmounted(() => {
  active = false;
});
</script>

<style scoped>
.settings-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
}

.settings-dialog {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25);
  width: 360px;
  max-width: 90%;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #26c6da;
  color: #fff;
  flex: 0 0 auto;
}

.settings-header h4 {
  margin: 0;
  font-size: 1em;
  font-weight: 600;
}

.settings-close-btn {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  padding: 2px 6px;
  border-radius: 4px;
}

.settings-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.settings-body {
  padding: 20px 16px;
  overflow-y: auto;
  min-height: 0;
}

.settings-gap {
  margin-top: 16px;
}

.settings-notes {
  resize: vertical;
  font-family: inherit;
}

.settings-setpoint {
  width: 80px;
}

.settings-label {
  display: block;
  font-size: 0.85em;
  color: #555;
  margin-bottom: 6px;
  font-weight: 500;
}

.settings-input {
  width: 100%;
  padding: 8px 10px;
  font-size: 0.95em;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.settings-input:focus {
  outline: none;
  border-color: #26c6da;
  box-shadow: 0 0 3px rgba(38, 198, 218, 0.4);
}

.interval-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.interval-input {
  width: 80px !important;
  flex: 0 0 auto;
}

.interval-unit-select {
  padding: 8px 10px;
  font-size: 0.95em;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
  transition: border-color 0.2s;
}

.interval-hint {
  margin: 6px 0 0;
  color: #c62828;
  font-size: 0.85em;
}

.interval-unit-select:focus {
  outline: none;
  border-color: #26c6da;
  box-shadow: 0 0 3px rgba(38, 198, 218, 0.4);
}

.settings-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #eee;
  background: #fafafa;
  flex: 0 0 auto;
}

.settings-erase-btn {
  margin-right: auto;
  padding: 6px 14px;
  font-size: 0.85em;
  border-radius: 4px;
  cursor: pointer;
  background: #26c6da;
  color: #fff;
  border: 1px solid #26c6da;
  border-color: #26c6da;
}

.settings-erase-btn:hover:not(:disabled) {
  background: #1ba3b3;
}

.settings-erase-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.settings-cancel-btn,
.settings-save-btn {
  padding: 6px 18px;
  font-size: 0.85em;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #ccc;
  transition: all 0.2s;
}

.settings-cancel-btn {
  background: #fff;
  color: #555;
}

.settings-cancel-btn:hover {
  background: #f5f5f5;
}

.settings-save-btn {
  background: #26c6da;
  color: #fff;
  border-color: #26c6da;
}

.settings-save-btn:hover:not(:disabled) {
  background: #1ba3b3;
}

.settings-save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
